/* eslint-disable max-lines */
/**
 * SlideSenseHelp
 *
 * Static user guide for Slide Sense, rendered as a normal React page inside
 * the SlideSense tab (alongside Library / Admin / Usage). All styling uses
 * Tailwind + design tokens (DESIGN.md) — no bespoke CSS, no iframe.
 *
 * Strings live under `slideSense.help.*` in the locale files. Plain strings
 * use `t()`; paragraphs with embedded JSX (chips, kbd, bold, code, italics)
 * use the `<Trans>` component with named placeholders.
 *
 * Screenshots live next to this file under `../help/screenshots/` and are
 * imported as Vite assets so they get bundled and served via `/assets/*`.
 */

import { Trans, useTranslation } from "react-i18next"

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Download,
  Info,
  Layers,
  Lightbulb,
  Monitor,
  Play,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
} from "@/components/icons"
import { Card, CardContent } from "@/components/ui/card"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import shot01 from "../help/screenshots/01-all-slides.jpg"
import shot02 from "../help/screenshots/02-deck-expanded.jpg"
import shot03 from "../help/screenshots/03-slide-preview.jpg"
import shot04 from "../help/screenshots/04-builder-with-slide.jpg"
import shot05 from "../help/screenshots/05-ai-find-results.jpg"
import shot06 from "../help/screenshots/06-save-dialog.jpg"
import shot07 from "../help/screenshots/07-my-presentations.jpg"
import shot08 from "../help/screenshots/08-present-dropdown.jpg"

// ---------------------------------------------------------------------------
// Reusable inline UI bits
// ---------------------------------------------------------------------------

function Kbd({ children }: { children?: React.ReactNode }) {
  return (
    <kbd className="border-border bg-background text-foreground inline-block rounded-md border border-b-2 px-1.5 py-px font-mono text-[12px] leading-tight shadow-sm">
      {children}
    </kbd>
  )
}

function UiChip({
  children,
  variant = "default",
}: {
  children: React.ReactNode
  variant?: "default" | "primary" | "muted" | "success" | "destructive"
}) {
  const variants: Record<typeof variant, string> = {
    default: "border-border bg-background text-foreground",
    primary: "border-primary bg-primary text-primary-foreground",
    muted: "border-border bg-muted text-muted-foreground",
    success: STATUS_TONES.success.solid,
    destructive: "border-destructive/30 bg-background text-destructive",
  } as const
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 align-middle text-[13px]",
        variants[variant]
      )}
    >
      {children}
    </span>
  )
}

function CategoryBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-foreground bg-accent text-accent-foreground inline-block rounded-md border px-1.5 py-px text-[11px] font-bold tracking-wide">
      {children}
    </span>
  )
}

function Callout({
  variant,
  icon: Icon,
  children,
}: {
  variant: "tip" | "info" | "warn"
  icon: typeof Lightbulb
  children: React.ReactNode
}) {
  const variants: Record<typeof variant, string> = {
    tip: STATUS_TONES.info.soft,
    info: STATUS_TONES.info.soft,
    warn: "border-destructive/25 bg-destructive/5 text-foreground",
  } as const
  const iconColor: Record<typeof variant, string> = {
    tip: STATUS_TONES.info.text,
    info: STATUS_TONES.info.text,
    warn: "text-destructive",
  } as const
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3.5 text-sm leading-relaxed",
        variants[variant]
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 flex-shrink-0", iconColor[variant])} />
      <div className="flex-1">{children}</div>
    </div>
  )
}

function Figure({ src, alt, children }: { src: string; alt: string; children?: React.ReactNode }) {
  return (
    <figure className="my-5">
      <img alt={alt} className="border-border bg-card w-full rounded-lg border shadow" src={src} />
      {children && (
        <figcaption className="text-muted-foreground mt-2.5 text-[13px] leading-relaxed">
          {children}
        </figcaption>
      )}
    </figure>
  )
}

function SectionHeading({ num, children }: { num: number; children: React.ReactNode }) {
  return (
    <h2
      className="font-display mb-2 flex items-center gap-3 text-[28px] leading-tight tracking-tight"
      id={`section-${num}`}
    >
      <span className="border-foreground bg-primary text-primary-foreground inline-flex h-8 w-8 items-center justify-center rounded-full border text-[15px] font-bold">
        {num}
      </span>
      {children}
    </h2>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Search
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="border-border bg-card rounded-lg border p-5">
      <div className="bg-primary/10 text-primary mb-2.5 inline-flex h-9 w-9 items-center justify-center rounded-lg">
        <Icon className="h-[18px] w-[18px]" />
      </div>
      <h3 className="mb-1 text-base font-semibold">{title}</h3>
      <p className="text-muted-foreground m-0 text-sm leading-relaxed">{children}</p>
    </div>
  )
}

function Step({ children }: { children: React.ReactNode }) {
  return <li className="pb-3 pl-2 leading-relaxed">{children}</li>
}

// ---------------------------------------------------------------------------
// Shared Trans component maps
// ---------------------------------------------------------------------------

/** Inline-formatting tags used in many help strings. */
const inlineFmt = {
  b: <strong className="text-foreground font-bold" />,
  i: <em />,
  code: <code className="bg-muted rounded px-1 py-0.5 font-mono text-[0.88em]" />,
  kbd: <Kbd />,
} as const

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SlideSenseHelp() {
  const { t } = useTranslation()

  // Chips that appear inside translation strings. Defined once here so the
  // Trans `components` maps stay readable below.
  const chipChevron = (
    <UiChip>
      <ChevronRight className="h-3.5 w-3.5" />
    </UiChip>
  )
  const chipPlainSearch = (
    <UiChip variant="muted">
      <Search className="h-3.5 w-3.5" />
      {t("slideSense.help.find.plainChip")}
    </UiChip>
  )
  const chipAiFind = (
    <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-md border border-transparent px-2 py-0.5 align-middle text-[13px]">
      <Sparkles className="h-3.5 w-3.5" />
      {t("slideSense.help.find.aiChip")}
    </span>
  )
  const chipAddBuilder = (
    <UiChip variant="primary">
      <Plus className="h-3.5 w-3.5" />
      {t("slideSense.help.preview.cardAddChip")}
    </UiChip>
  )
  const chipPlusOnly = (
    <UiChip variant="primary">
      <Plus className="h-3 w-3" strokeWidth={2.5} />
    </UiChip>
  )
  const chipAddDeck = (
    <UiChip>
      <Layers className="h-3.5 w-3.5" />
      {t("slideSense.help.build.addDeckChip")}
    </UiChip>
  )
  const chipTrashOnly = (
    <UiChip>
      <Trash2 className="h-3.5 w-3.5" />
    </UiChip>
  )
  const chipClear = (
    <UiChip>
      <Trash2 className="h-3.5 w-3.5" />
      {t("slideSense.help.build.clearChip")}
    </UiChip>
  )
  const chipSave = (
    <UiChip>
      <Save className="h-3.5 w-3.5" />
      {t("slideSense.help.save.saveChip")}
    </UiChip>
  )
  const chipLoad = (
    <UiChip>
      <Play className="h-3.5 w-3.5" />
      {t("slideSense.help.save.loadChip")}
    </UiChip>
  )
  const chipDownload = (
    <UiChip>
      <Download className="h-3.5 w-3.5" />
      {t("slideSense.help.save.downloadChip")}
    </UiChip>
  )
  const chipDelete = (
    <UiChip variant="destructive">
      <Trash2 className="h-3.5 w-3.5" />
      {t("slideSense.help.save.deleteChip")}
    </UiChip>
  )
  const chipDownloadOnly = (
    <UiChip>
      <Download className="h-3.5 w-3.5" />
      {t("slideSense.help.download.downloadChip")}
    </UiChip>
  )

  // Category badges grid — slotted into the readingCategory Trans key.
  const categoryBadges = (
    <span>
      <CategoryBadge>{t("slideSense.categoryTitle")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryContent")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryChart")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryTable")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryImage")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryDivider")}</CategoryBadge>{" "}
      <CategoryBadge>{t("slideSense.categoryClosing")}</CategoryBadge>
    </span>
  )

  const tocItems: { label: string }[] = [
    { label: t("slideSense.help.toc.find") },
    { label: t("slideSense.help.toc.preview") },
    { label: t("slideSense.help.toc.build") },
    { label: t("slideSense.help.toc.save") },
    { label: t("slideSense.help.toc.download") },
    { label: t("slideSense.help.toc.present") },
    { label: t("slideSense.help.toc.tips") },
  ]

  const shortcuts: [React.ReactNode, string][] = [
    [<Kbd key="esc">Esc</Kbd>, t("slideSense.help.present.shortcutExit")],
    [
      <>
        <Kbd>←</Kbd> <Kbd>→</Kbd>
      </>,
      t("slideSense.help.present.shortcutPrevNext"),
    ],
    [<Kbd key="l">L</Kbd>, t("slideSense.help.present.shortcutLaser")],
    [<Kbd key="d">D</Kbd>, t("slideSense.help.present.shortcutPen")],
    [<Kbd key="s">S</Kbd>, t("slideSense.help.present.shortcutStrip")],
  ]

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1080px] px-8 py-14 max-md:px-5 max-md:pt-8 max-md:pb-16">
        {/* ── Header ───────────────────────────────────────────────── */}
        <header className="mb-10 flex flex-col gap-3.5">
          <span className="text-muted-foreground inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.08em] uppercase">
            <span className="bg-primary h-2 w-2 rounded-full" />
            {t("slideSense.help.eyebrow")}
          </span>
          <h1 className="font-display m-0 text-[40px] leading-[1.1] tracking-tight">
            {t("slideSense.help.title")}
          </h1>
          <p className="text-muted-foreground m-0 max-w-[64ch] text-[17px] leading-relaxed">
            {t("slideSense.help.lede")}
          </p>
        </header>

        {/* ── Hero shot ────────────────────────────────────────────── */}
        <Figure alt={t("slideSense.help.heroAlt")} src={shot01}>
          <Trans components={inlineFmt} i18nKey="slideSense.help.heroCaption" />
        </Figure>

        {/* ── Feature grid ────────────────────────────────────────── */}
        <div className="my-6 grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <FeatureCard icon={Search} title={t("slideSense.help.features.findTitle")}>
            {t("slideSense.help.features.findBody")}
          </FeatureCard>
          <FeatureCard icon={Layers} title={t("slideSense.help.features.buildTitle")}>
            {t("slideSense.help.features.buildBody")}
          </FeatureCard>
          <FeatureCard icon={Download} title={t("slideSense.help.features.exportTitle")}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.features.exportBody" />
          </FeatureCard>
          <FeatureCard icon={Play} title={t("slideSense.help.features.presentTitle")}>
            {t("slideSense.help.features.presentBody")}
          </FeatureCard>
        </div>

        {/* ── TOC ──────────────────────────────────────────────────── */}
        <nav
          aria-label={t("slideSense.help.toc.title")}
          className="border-border bg-card mt-8 rounded-lg border p-5"
        >
          <h4 className="text-muted-foreground mb-2.5 text-[11px] font-bold tracking-[0.08em] uppercase">
            {t("slideSense.help.toc.title")}
          </h4>
          <ol className="m-0 grid list-none grid-cols-2 gap-x-6 gap-y-1 p-0 max-md:grid-cols-1">
            {tocItems.map(({ label }, idx) => (
              <li key={label} className="py-1">
                <a
                  className="text-foreground hover:text-primary text-sm font-medium hover:underline"
                  href={`#section-${idx + 1}`}
                >
                  <span className="text-muted-foreground mr-1 font-mono">{idx + 1}.</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ── 1. Finding slides ───────────────────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={1}>{t("slideSense.help.find.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            <Trans components={inlineFmt} i18nKey="slideSense.help.find.intro" />
          </p>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.find.browseHeading")}
          </h3>
          <p className="mb-3 leading-relaxed">
            <Trans
              components={{ ...inlineFmt, chip: chipChevron }}
              i18nKey="slideSense.help.find.browseBody"
            />
          </p>
          <Callout icon={Info} variant="info">
            {t("slideSense.help.find.browseCallout")}
          </Callout>

          <Figure alt={t("slideSense.help.find.browseAlt")} src={shot02}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.find.browseCaption" />
          </Figure>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.find.searchHeading")}
          </h3>
          <p className="mb-3 leading-relaxed">{t("slideSense.help.find.searchIntro")}</p>
          <div className="my-4 grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <Card>
              <CardContent className="p-4 pt-4">
                <div className="mb-2">{chipPlainSearch}</div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  {t("slideSense.help.find.plainBody")}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 pt-4">
                <div className="mb-2">{chipAiFind}</div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.find.aiBody" />
                </p>
              </CardContent>
            </Card>
          </div>

          <Callout icon={CheckCircle2} variant="tip">
            <Trans components={inlineFmt} i18nKey="slideSense.help.find.scopeCallout" />
          </Callout>

          <Figure alt={t("slideSense.help.find.aiResultsAlt")} src={shot05}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.find.aiResultsCaption" />
          </Figure>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.find.readingHeading")}
          </h3>
          <p className="mb-2 leading-relaxed">{t("slideSense.help.find.readingIntro")}</p>
          <ul className="border-border m-0 list-none border-t p-0">
            <li className="border-border border-b border-dashed py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.find.readingThumb" />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.find.readingTitle" />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans
                components={{ ...inlineFmt, badges: categoryBadges }}
                i18nKey="slideSense.help.find.readingCategory"
              />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.find.readingSource" />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.find.readingUpdated" />
            </li>
            <li className="py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.find.readingExcerpt" />
            </li>
          </ul>
        </section>

        {/* ── 2. Previewing a slide ───────────────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={2}>{t("slideSense.help.preview.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            {t("slideSense.help.preview.intro")}
          </p>

          <Figure alt={t("slideSense.help.preview.alt")} src={shot03}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.preview.caption" />
          </Figure>

          <Card>
            <CardContent className="p-5 pt-5">
              <p className="m-0 mb-2">{t("slideSense.help.preview.cardIntro")}</p>
              <ul className="border-border m-0 list-none border-t p-0">
                <li className="border-border border-b border-dashed py-2">
                  {t("slideSense.help.preview.cardFull")}
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.preview.cardSummary" />
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.preview.cardKeywords" />
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.preview.cardCounter" />
                </li>
                <li className="py-2">
                  <Trans
                    components={{ ...inlineFmt, chip: chipAddBuilder }}
                    i18nKey="slideSense.help.preview.cardAdd"
                  />
                </li>
              </ul>
            </CardContent>
          </Card>

          <div className="mt-3">
            <Callout icon={Lightbulb} variant="tip">
              <Trans components={inlineFmt} i18nKey="slideSense.help.preview.tip" />
            </Callout>
          </div>
        </section>

        {/* ── 3. Building your presentation ──────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={3}>{t("slideSense.help.build.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            <Trans components={inlineFmt} i18nKey="slideSense.help.build.intro" />
          </p>

          <Figure alt={t("slideSense.help.build.alt")} src={shot04}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.build.caption" />
          </Figure>

          <ol className="marker:text-foreground my-3 list-decimal space-y-2 pl-6 leading-relaxed marker:font-bold">
            <Step>
              <Trans
                components={{ ...inlineFmt, chip: chipPlusOnly }}
                i18nKey="slideSense.help.build.stepHover"
              />
            </Step>
            <Step>
              <Trans components={inlineFmt} i18nKey="slideSense.help.build.stepDrag" />
            </Step>
            <Step>
              <Trans
                components={{ ...inlineFmt, chip: chipAddDeck }}
                i18nKey="slideSense.help.build.stepAddDeck"
              />
            </Step>
          </ol>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.build.rearrangeHeading")}
          </h3>
          <ul className="border-border m-0 list-none border-t p-0">
            <li className="border-border border-b border-dashed py-2">
              <Trans components={inlineFmt} i18nKey="slideSense.help.build.rearrangeReorder" />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans
                components={{ ...inlineFmt, chip: chipTrashOnly }}
                i18nKey="slideSense.help.build.rearrangeRemove"
              />
            </li>
            <li className="py-2">
              <Trans
                components={{ ...inlineFmt, chip: chipClear }}
                i18nKey="slideSense.help.build.rearrangeClear"
              />
            </li>
          </ul>

          <div className="mt-3">
            <Callout icon={Monitor} variant="info">
              <Trans components={inlineFmt} i18nKey="slideSense.help.build.mobileCallout" />
            </Callout>
          </div>
        </section>

        {/* ── 4. Saving as a presentation ─────────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={4}>{t("slideSense.help.save.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            {t("slideSense.help.save.intro")}
          </p>

          <ol className="marker:text-foreground my-3 list-decimal space-y-2 pl-6 leading-relaxed marker:font-bold">
            <Step>
              <Trans
                components={{ ...inlineFmt, chip: chipSave }}
                i18nKey="slideSense.help.save.stepSave"
              />
            </Step>
            <Step>
              <Trans components={inlineFmt} i18nKey="slideSense.help.save.stepName" />
            </Step>
            <Step>
              <Trans components={inlineFmt} i18nKey="slideSense.help.save.stepClick" />
            </Step>
          </ol>

          <div className="my-5 grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <Figure alt={t("slideSense.help.save.dialogAlt")} src={shot06}>
              <Trans components={inlineFmt} i18nKey="slideSense.help.save.dialogCaption" />
            </Figure>
            <Figure alt={t("slideSense.help.save.listAlt")} src={shot07}>
              <Trans components={inlineFmt} i18nKey="slideSense.help.save.listCaption" />
            </Figure>
          </div>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.save.workingHeading")}
          </h3>
          <p className="mb-2 leading-relaxed">
            <Trans components={inlineFmt} i18nKey="slideSense.help.save.workingIntro" />
          </p>
          <ul className="border-border m-0 list-none border-t p-0">
            <li className="border-border border-b border-dashed py-2">
              <Trans
                components={{ ...inlineFmt, chip: chipLoad }}
                i18nKey="slideSense.help.save.workingLoad"
              />
            </li>
            <li className="border-border border-b border-dashed py-2">
              <Trans
                components={{ ...inlineFmt, chip: chipDownload }}
                i18nKey="slideSense.help.save.workingDownload"
              />
            </li>
            <li className="py-2">
              <Trans
                components={{ ...inlineFmt, chip: chipDelete }}
                i18nKey="slideSense.help.save.workingDelete"
              />
            </li>
          </ul>

          <div className="mt-3">
            <Callout icon={AlertTriangle} variant="warn">
              <Trans components={inlineFmt} i18nKey="slideSense.help.save.warn" />
            </Callout>
          </div>
        </section>

        {/* ── 5. Downloading as PowerPoint ───────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={5}>{t("slideSense.help.download.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            <Trans components={inlineFmt} i18nKey="slideSense.help.download.intro" />
          </p>
          <p className="leading-relaxed">
            <Trans
              components={{ ...inlineFmt, chip: chipDownloadOnly }}
              i18nKey="slideSense.help.download.body"
            />
          </p>
        </section>

        {/* ── 6. Presenting ──────────────────────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={6}>{t("slideSense.help.present.title")}</SectionHeading>
          <p className="text-muted-foreground mb-6 max-w-[70ch] leading-relaxed">
            {t("slideSense.help.present.intro")}
          </p>

          <Figure alt={t("slideSense.help.present.alt")} src={shot08}>
            <Trans components={inlineFmt} i18nKey="slideSense.help.present.caption" />
          </Figure>

          <div className="my-4 grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <Card>
              <CardContent className="p-4 pt-4">
                <div className="mb-2">
                  <UiChip>
                    <Play className="h-3.5 w-3.5" fill="currentColor" />
                    {t("slideSense.help.present.modePresentChip")}
                  </UiChip>
                </div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  {t("slideSense.help.present.modePresentBody")}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 pt-4">
                <div className="mb-2">
                  <UiChip>
                    <Monitor className="h-3.5 w-3.5" />
                    {t("slideSense.help.present.modePresenterChip")}
                  </UiChip>
                </div>
                <p className="text-muted-foreground m-0 text-sm leading-relaxed">
                  {t("slideSense.help.present.modePresenterBody")}
                </p>
              </CardContent>
            </Card>
          </div>

          <h3 className="mt-7 mb-2 text-lg font-semibold tracking-tight">
            {t("slideSense.help.present.shortcutsHeading")}
          </h3>
          <table className="mt-2 w-full border-collapse text-sm">
            <tbody>
              {shortcuts.map(([keys, label], idx) => (
                <tr key={idx}>
                  <td
                    className={cn(
                      "w-32 py-2 pr-2 whitespace-nowrap",
                      idx < shortcuts.length - 1 && "border-border border-b border-dashed"
                    )}
                  >
                    {keys}
                  </td>
                  <td
                    className={cn(
                      "py-2",
                      idx < shortcuts.length - 1 && "border-border border-b border-dashed"
                    )}
                  >
                    {label}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3">
            <Callout icon={Lightbulb} variant="tip">
              {t("slideSense.help.present.popupTip")}
            </Callout>
          </div>
        </section>

        {/* ── 7. Tips & shortcuts ────────────────────────────────── */}
        <section className="mt-14 scroll-mt-4">
          <SectionHeading num={7}>{t("slideSense.help.tips.title")}</SectionHeading>
          <Card>
            <CardContent className="p-5 pt-5">
              <ul className="border-border m-0 list-none border-t p-0">
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.tips.search" />
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.tips.bookmark" />
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.tips.folder" />
                </li>
                <li className="border-border border-b border-dashed py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.tips.failed" />
                </li>
                <li className="py-2">
                  <Trans components={inlineFmt} i18nKey="slideSense.help.tips.removed" />
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        <footer className="border-border text-muted-foreground/80 mt-20 flex flex-wrap justify-between gap-3 border-t pt-6 text-xs">
          <span>{t("slideSense.help.footerLeft")}</span>
          <span>{t("slideSense.help.footerRight")}</span>
        </footer>
      </div>
    </div>
  )
}
